import { accessToken$, impersonateEmail$ } from "$root/functional-areas/session/atom";
import type { ConversationMinInfo } from "@eu.mdotm.api/sphere";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import type { UIMessage } from "ai";
import { sphereAIConfig } from "./config";
import { rateLimitedFrom } from "./rateLimit";

/**
 * Server-side chat persistence (MDOT-24442) — the sphere-mcp
 * /v1/conversations surface. Replaces the IndexedDB stopgap
 * (localChatStore.ts): the full `UIMessage[]` payload (tool-call parts
 * included) is stored as an opaque blob on the server, so a conversation
 * survives a reload, a cache clear, and a device switch — and is scoped to
 * the signed-in (or impersonated) user instead of the browser profile.
 *
 * CROSS-REPO CONTRACT, kept in sync by hand: the server side of every shape
 * here is `history-store.ts` / `history-routes.ts` in sphere-mcp
 * (src/server/chat). The summary shape is assigned to the generated
 * `ConversationMinInfo`, so a field rename on either side silently empties
 * the History tab.
 *
 * Note: per-message feedback (`setConversationMessageFeedback`) still targets
 * the Java backend, which doesn't know these conversation ids — unchanged
 * from the IndexedDB era.
 *
 * This module is the WIRE only. The collection half of it — the History list and the metadata
 * writes — is consumed through `repository.ts`, which owns the query key and the
 * invalidate-after-write; call sites should go there rather than here. The transcript is READ
 * only ({@link getConversation}): sphere-mcp stores every turn itself (MDOT-25078), so this tab
 * never writes one.
 */

/**
 * List row for the History tab: conversation metadata, plus the `favouritedAt` that the generated
 * {@link ConversationMinInfo} doesn't carry. It orders the Favourites group (most recently
 * favourited on top) and is stamped SERVER-side when a conversation is favourited — the client
 * never sends it, so a wrong system clock can't skew the order. Absent while not favourite, and on
 * a conversation favourited before the column existed; `favouritedMs` (historyGrouping.ts) then
 * falls back to activity time, matching how the server sorts the same rows.
 */
export type ConversationInfo = ConversationMinInfo & { favouritedAt?: string };

/** A conversation as returned whole by GET /v1/conversations/:id. */
export type StoredConversation = ConversationMinInfo & {
	uuid: string;
	favourite: boolean;
	messages: UIMessage[];
	/**
	 * Per-tool-call `structuredContent`, on conversations stored before MDOT-25078 only — opaque, and
	 * never written any more. Read back by `hydrateToolResults` (toolResultStore.ts).
	 */
	toolResults?: unknown;
};

function conversationUrl(uuid?: string): string {
	// Same base as the chat transport (`{apiUrl}/chat`) — one deploy serves both.
	return `${sphereAIConfig().apiUrl}/conversations${uuid ? `/${uuid}` : ""}`;
}

/** The identity headers every Sphere AI wire sends (chat transport, MCP, uploads). */
function authHeaders(): Record<string, string> {
	const accessToken = readAtom(accessToken$);
	const impersonateEmail = readAtom(impersonateEmail$);
	return {
		...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
		...(impersonateEmail ? { "X-Impersonate-Email": impersonateEmail } : {}),
	};
}

async function request(method: string, url: string, body?: unknown, passStatuses?: number[]): Promise<Response> {
	const response = await fetch(url, {
		method,
		headers: {
			...authHeaders(),
			...(body !== undefined ? { "Content-Type": "application/json" } : {}),
		},
		body: body !== undefined ? JSON.stringify(body) : undefined,
	});
	// A refusal to protect the per-user budget is not a broken request, and the caller that
	// surfaces it needs to say WHEN rather than just "went wrong" — so it keeps its own type and
	// carries the server's own delay (./rateLimit.ts). Every other non-ok status is a plain
	// failure; 404 stays non-throwing because it is this surface's routine "nothing stored yet".
	if (response.status === 429) {
		throw rateLimitedFrom(response);
	}
	if (!response.ok && response.status !== 404 && !passStatuses?.includes(response.status)) {
		throw new Error(`Conversation request failed (${method} ${url}: HTTP ${response.status})`);
	}
	return response;
}

/**
 * Conversation list for the sidebar/history. Metadata only; already ordered favourites-first
 * (most recently favourited on top), then by newest activity — `groupHistory` re-sorts on the
 * same keys, so the two never disagree.
 */
export async function listConversations(): Promise<ConversationInfo[]> {
	const response = await request("GET", conversationUrl());
	const payload = (await response.json()) as { conversations: ConversationInfo[] };
	return payload.conversations;
}

/**
 * A conversation's full payload plus its conversation-level timestamps, or
 * `null` if nothing is stored under this id — the routine answer for a fresh
 * chat, whose id is minted before the first turn ever finishes.
 */
export async function getConversation(uuid: string): Promise<StoredConversation | null> {
	const response = await request("GET", conversationUrl(uuid));
	if (response.status === 404) {
		return null;
	}
	return (await response.json()) as StoredConversation;
}

export async function deleteConversation(uuid: string): Promise<void> {
	await request("DELETE", conversationUrl(uuid));
}

export async function renameConversation(uuid: string, name: string): Promise<void> {
	await request("PATCH", conversationUrl(uuid), { name });
}

/**
 * Toggle a conversation's favourite flag. The server stamps `favouritedAt` from its own clock, so
 * re-favouriting an already-favourite conversation moves it back to the top of Favourites.
 */
export async function setConversationFavourite(uuid: string, favourite: boolean): Promise<void> {
	await request("PATCH", conversationUrl(uuid), { favourite });
}
